/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  BaseSelection,
  LexicalNode,
} from "lexical";
import { useEffect, useState } from "react";
import { getSelectedNode } from "../../utils/getSelectedNode";
import {
  ContextMenu,
  ContextMenuDivider,
  ContextMenuItem,
} from "./ContextMenu";

export type Context = {
  selectedNode: LexicalNode | null;
  selection: BaseSelection | null;
};

type MenuItemOption = {
  type: "item";
  title: string;
  shortcut: string | null;
  icon: JSX.Element | null;
  $onSelect: () => void;
  $disableOn?: (ctx: Context) => boolean;
  $showOn?: (ctx: Context) => boolean;
};

type MenuDividerOption = {
  type: "divider";
  $showOn?: (ctx: Context) => boolean;
};

export type ContextMenuOption = MenuItemOption | MenuDividerOption;

type ContextMenuPluginProps = {
  items: ContextMenuOption[];
};

export const ContextMenuPlugin = ({ items }: ContextMenuPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const [context, setContext] = useState<Context>({
    selectedNode: null,
    selection: null,
  });

  useEffect(() => {
    function onContextMenu(e: MouseEvent) {
      e.preventDefault();

      editor.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setContext({
            selectedNode: getSelectedNode(selection),
            selection: selection,
          });
        } else if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          setContext({
            selectedNode: nodes[0] ?? null,
            selection: selection,
          });
        } else {
          setContext({
            selectedNode: null,
            selection: null,
          });
        }
      });

      setAnchor({ x: e.clientX, y: e.clientY });
    }

    return editor.registerRootListener((rootElement, prevRootElement) => {
      if (prevRootElement !== null) {
        prevRootElement.removeEventListener("contextmenu", onContextMenu);
      }
      if (rootElement !== null) {
        rootElement.addEventListener("contextmenu", onContextMenu);
      }
    });
  }, [editor]);

  return (
    <ContextMenu
      isOpen={anchor !== null}
      anchor={anchor}
      onClose={() => setAnchor(null)}
    >
      {items.map((item, index) => {
        if (item.$showOn && !item.$showOn(context)) return null;

        if (item.type === "item") {
          const { title, shortcut, icon, $onSelect, $disableOn } = item;
          return (
            <ContextMenuItem
              key={`context-menu-item-${index}`}
              title={title}
              shortcut={shortcut}
              icon={icon}
              onClick={() => $onSelect()}
              isDisabled={$disableOn ? $disableOn(context) : false}
            />
          );
        }

        if (item.type === "divider") {
          return <ContextMenuDivider key={`context-menu-divider-${index}`} />;
        }

        return null;
      })}
    </ContextMenu>
  );
};
